"use client";

import { c as _c } from "react/compiler-runtime";
import { useActionState, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { type FeedbackActionState, submitFeedbackAction } from "@/app/contact/_actions/submit-feedback";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
type SessionUser = {
  name: string | null;
  email: string | null;
  phone: string | null;
};
type ContactFormProps = {
  sessionUser: SessionUser | null;
};
const MESSAGE_MIN = 10;
const MESSAGE_MAX = 5000;
const NAME_MIN = 2;
const NAME_MAX = 120;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FIELD_HEIGHT = "h-10";
const FIELD_TEXT = "text-sm";
type FieldErrors = Partial<Record<"submitterName" | "submitterEmail" | "message", string>>;
function useFeedbackToast(state, formRef) {
  const $ = _c(4);
  const prev = useRef(null);
  let t0;
  let t1;
  if ($[0] !== formRef || $[1] !== state) {
    t0 = () => {
      if (!state || state === prev.current) {
        return;
      }
      prev.current = state;
      if (state.status === "success") {
        toast.success(state.message);
        formRef.current?.reset();
      } else {
        toast.error(state.message);
      }
    };
    t1 = [state, formRef];
    $[0] = formRef;
    $[1] = state;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}
function validateForm(formData: FormData, isGuest: boolean): FieldErrors {
  const errors: FieldErrors = {};
  const message = (formData.get("message") as string | null)?.trim() ?? "";
  if (message.length === 0) {
    errors.message = "Add a message.";
  } else if (message.length < MESSAGE_MIN) {
    errors.message = `Message must be at least ${MESSAGE_MIN} characters.`;
  }
  if (isGuest) {
    const name = (formData.get("submitterName") as string | null)?.trim() ?? "";
    if (name.length === 0) {
      errors.submitterName = "Add your name.";
    } else if (name.length < NAME_MIN) {
      errors.submitterName = `Name must be at least ${NAME_MIN} characters.`;
    }
    const email = (formData.get("submitterEmail") as string | null)?.trim() ?? "";
    if (email.length === 0) {
      errors.submitterEmail = "Add your email.";
    } else if (!EMAIL_RE.test(email)) {
      errors.submitterEmail = "Enter a valid email address.";
    }
  }
  return errors;
}
function FieldError(t0) {
  const $ = _c(2);
  const {
    message
  } = t0;
  if (!message) {
    return null;
  }
  let t1;
  if ($[0] !== message) {
    t1 = <p className="text-destructive text-sm">{message}</p>;
    $[0] = message;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function ContactForm(t0) {
  const $ = _c(37);
  const {
    sessionUser
  } = t0;
  const formRef = useRef(null);
  const [state, formAction, isPending] = useActionState(submitFeedbackAction, null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [errors, setErrors] = useState(t1);
  useFeedbackToast(state, formRef);
  let t2;
  if ($[1] !== state?.status) {
    t2 = () => {
      if (state?.status === "success") {
        setErrors({});
      }
    };
    $[1] = state?.status;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== state) {
    t3 = [state];
    $[3] = state;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  const isGuest = sessionUser === null;
  let t4;
  if ($[5] !== isGuest) {
    t4 = function handleSubmit(event) {
      const fd = new FormData(event.currentTarget);
      const fieldErrors = validateForm(fd, isGuest);
      if (Object.keys(fieldErrors).length > 0) {
        event.preventDefault();
        setErrors(fieldErrors);
        return;
      }
      setErrors({});
    };
    $[5] = isGuest;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleSubmit = t4;
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <input type="hidden" name="pageUrl" value="/contact" />;
    t6 = <input type="hidden" name="appArea" value="contact" />;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== errors.submitterEmail || $[10] !== errors.submitterName || $[11] !== isGuest) {
    t7 = isGuest ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2"><div className="space-y-2"><Label htmlFor="submitterName">Your name <span className="text-destructive">*</span></Label><Input id="submitterName" name="submitterName" autoComplete="name" placeholder="Full name" maxLength={NAME_MAX} aria-invalid={errors.submitterName ? true : undefined} className={cn(FIELD_HEIGHT, FIELD_TEXT, errors.submitterName && "border-destructive")} /><FieldError message={errors.submitterName} /></div><div className="space-y-2"><Label htmlFor="submitterEmail">Email <span className="text-destructive">*</span></Label><Input id="submitterEmail" name="submitterEmail" type="email" autoComplete="email" placeholder="you@example.com" maxLength={254} aria-invalid={errors.submitterEmail ? true : undefined} className={cn(FIELD_HEIGHT, FIELD_TEXT, errors.submitterEmail && "border-destructive")} /><FieldError message={errors.submitterEmail} /></div></div> : null;
    $[9] = errors.submitterEmail;
    $[10] = errors.submitterName;
    $[11] = isGuest;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Label htmlFor="type">Type <span className="text-destructive">*</span></Label>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SelectTrigger id="type" className={cn("w-full", FIELD_HEIGHT, FIELD_TEXT)}><SelectValue placeholder="Choose a feedback type" /></SelectTrigger>;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="space-y-2">{t8}<Select name="type" defaultValue="bug_report">{t9}<SelectContent><SelectItem value="bug_report">Bug report</SelectItem><SelectItem value="suggestion">Suggestion</SelectItem><SelectItem value="complaint">Complaint</SelectItem><SelectItem value="data_issue">Data issue</SelectItem><SelectItem value="support_request">Support request</SelectItem><SelectItem value="abuse_report">Abuse report</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Label htmlFor="message">Message <span className="text-destructive">*</span></Label>;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = errors.message && "border-destructive";
  let t13;
  if ($[17] !== t12) {
    t13 = cn("min-h-[160px] resize-none", FIELD_TEXT, t12);
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const t14 = errors.message ? true : undefined;
  let t15;
  if ($[19] !== t13 || $[20] !== t14) {
    t15 = <Textarea name="message" id="message" className={t13} placeholder="Tell us what happened, what you expected, and anything else that will help us review it." maxLength={MESSAGE_MAX} aria-invalid={t14} />;
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== errors.message) {
    t16 = <FieldError message={errors.message} />;
    $[22] = errors.message;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t15 || $[25] !== t16) {
    t17 = <div className="space-y-2">{t11}{t15}{t16}</div>;
    $[24] = t15;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== isPending) {
    t18 = isPending ? <><Spinner className="size-4" />Submitting…</> : "Submit feedback";
    $[27] = isPending;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== isPending || $[30] !== t18) {
    t19 = <Button type="submit" size="lg" disabled={isPending} className="w-full sm:w-auto">{t18}</Button>;
    $[29] = isPending;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== handleSubmit || $[33] !== t17 || $[34] !== t19 || $[35] !== t7) {
    t20 = <form ref={formRef} action={formAction} onSubmit={handleSubmit} noValidate={true} className="space-y-6">{t5}{t6}{t7}{t10}{t17}{t19}</form>;
    $[32] = handleSubmit;
    $[33] = t17;
    $[34] = t19;
    $[35] = t7;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  return t20;
}